import { injectable, inject, LazyServiceIdentifier } from 'inversify';
import { Subscription } from 'rxjs';
import { LoginService, SessionState } from './login.service';
import { RefreshService } from './refresh.service';

type AuthMsg = { type: 'logout' } | { type: 'cookies-unavailable' };

@injectable()
export class CrossTabAuthService {
  private channel: BroadcastChannel | null = null;
  private applyingRemote = false;
  private lastState: SessionState | null = null;
  private subs: Subscription[] = [];

  constructor(
    // Deferred identifiers: cross-tab ⇄ login.service and cross-tab → refresh →
    // login are circular imports; defer the class references to plan time to avoid
    // a module-load TDZ in the webpack build.
    @inject(new LazyServiceIdentifier(() => LoginService)) private loginService: LoginService,
    @inject(new LazyServiceIdentifier(() => RefreshService)) private refreshService: RefreshService,
  ) {}

  init(): void {
    if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined' || this.channel) return;
    this.channel = new BroadcastChannel('xc-auth');
    this.channel.onmessage = (e: MessageEvent<AuthMsg>) => {
      this.applyingRemote = true;
      try {
        if (e.data?.type === 'logout') this.loginService.clearLocalSession();
        else if (e.data?.type === 'cookies-unavailable') this.refreshService.cookieSessionUnavailable = true;
      } finally {
        this.applyingRemote = false;
      }
    };
    this.subs.push(
      this.loginService.onSessionState.subscribe((s) => {
        if (!this.applyingRemote && this.lastState === SessionState.LOGGED_IN && s === SessionState.ANONYMOUS) {
          this.channel?.postMessage({ type: 'logout' } as AuthMsg);
        }
        this.lastState = s;
      }),
    );
    this.subs.push(
      this.refreshService.cookieUnavailable$.subscribe(() => {
        if (!this.applyingRemote) this.channel?.postMessage({ type: 'cookies-unavailable' } as AuthMsg);
      }),
    );
  }

  dispose(): void {
    this.subs.forEach((s) => s.unsubscribe());
    this.channel?.close();
    this.channel = null;
  }
}
